import { useState, useEffect } from 'react'

/**
 * Hook to debounce a value
 * @param value - Value to debounce
 * @param delay - Delay in milliseconds
 * @returns Debounced value
 */
export function useDebounce<T>(value: T, delay: number = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => clearTimeout(handler)
  }, [value, delay])

  return debouncedValue
}

/** @deprecated Use useDebounce instead */
export const useDebouncedValue = useDebounce

/**
 * Hook for debounced search with input state management
 * @param initialValue - Initial search value
 * @param delay - Debounce delay in milliseconds
 * @returns [debouncedValue, inputValue, setInputValue]
 */
export function useDebouncedSearch(
  initialValue: string = '',
  delay: number = 300
): [string, string, (value: string) => void] {
  const [inputValue, setInputValue] = useState(initialValue)
  const debouncedValue = useDebounce(inputValue, delay)

  return [debouncedValue, inputValue, setInputValue]
}
